/**
 * @file DesignsPanel.tsx
 * @description Панель «Дизайны» (phase19.16 Р1-6а, §7.9 фазы;
 * uiux-builder-shell.md §3.3, §9; решения пользователя 2026-10-12): строка
 * состояния «в работе / на сайте», [+ Создать новый] и [Сохранить текущий
 * вид как дизайн] столбиком, «Сохранено N из 20», карточки в порядке «в
 * работе → на сайте → остальные по дате». «Посмотреть» — канва показывает
 * дизайн, черновик не меняется; «Применить ко всему сайту» — с
 * подтверждением, в черновик; «Обновить из текущего вида» — окном. После
 * создания, применения и обновления карточка подсвечивается на 2 с.
 * Без права настроек магазина — объяснение (список сервер не отдаёт).
 */

import { Plus, Save, SwatchBook } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/EmptyState";
import { ListError } from "../../ui/ListStates";
import { Skeleton } from "../../ui/skeleton";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { CreateDesignDialog } from "./CreateDesignDialog";
import { DesignCard } from "./DesignCard";
import { DesignsStatusLine } from "./DesignsStatusLine";
import { SaveDesignDialog } from "./SaveDesignDialog";
import { UpdateDesignDialog } from "./UpdateDesignDialog";
import { useDesignActions } from "../../../hooks/site-builder/useDesignActions";
import { sortDesigns } from "../../../lib/site-view/design-status";
import type { SiteDesignItem } from "../../../lib/api-site-designs";

/** Кнопка во всю ширину панели с переносом подписи. */
const WIDE_BUTTON = "h-auto w-full whitespace-normal px-4 py-2 text-center";

export function DesignsPanel() {
  const b = useBuilder();
  const view = useSiteView();
  const { designs } = b;
  const actions = useDesignActions();
  const [renaming, setRenaming] = useState<SiteDesignItem | null>(null);
  const [updating, setUpdating] = useState<SiteDesignItem | null>(null);
  const [creating, setCreating] = useState(false);

  // «В работе» считает сервер по черновику: правки оформления — перечитать список.
  // Только когда «что изменено» действительно сменилось: первую загрузку делает
  // useDesigns (сравнение с прошлым значением, а не флаг — двойной запуск эффектов в StrictMode не дублирует запрос).
  const { reload } = designs;
  const enabled = b.canEditSettings;
  const seenChanges = useRef(view.changes);
  useEffect(() => {
    if (seenChanges.current === view.changes) return;
    seenChanges.current = view.changes;
    if (enabled) void reload();
  }, [enabled, reload, view.changes]);
  const draftHasLogos = Boolean(view.draft?.brand.logoLight || view.draft?.brand.logoDark);

  if (!b.canEditSettings) {
    return <EmptyState variant="forbidden" icon={SwatchBook} title="Дизайны недоступны" description="Дизайны сайта меняет владелец магазина или сотрудник с правом настроек магазина." />;
  }
  if (designs.error && !designs.data) return <ListError message={designs.error} onRetry={() => void designs.reload()} />;
  if (!designs.data) return <Skeleton className="h-40 rounded-xl" aria-label="Загрузка дизайнов" />;
  const list = designs.data;
  const { limit } = list;
  const full = limit.used >= limit.max;
  const items = sortDesigns(list.items);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <DesignsStatusLine items={list.items} />
      {/* На 360 подпись длинная: свои поля и перенос на 2 строки, высота растёт от минимума темы (48/44). */}
      <div className="flex flex-col gap-2">
        <Button variant="outline" className={WIDE_BUTTON} disabled={full} onClick={() => setCreating(true)}>
          <Plus aria-hidden />
          Создать новый
        </Button>
        <Button variant="outline" className={WIDE_BUTTON} disabled={full} onClick={b.openSaveDesign}>
          <Save aria-hidden />
          Сохранить текущий вид как дизайн
        </Button>
      </div>
      <span className="type-caption text-on-surface-variant">
        Сохранено {limit.used} из {limit.max}
        {full ? " — удалите ненужный дизайн, чтобы создать или сохранить новый" : ""}
      </span>
      {items.length === 0 ? (
        <p className="type-body text-on-surface-variant">Дизайнов пока нет. Создайте новый или сохраните текущий вид — вернуть его можно будет в один шаг.</p>
      ) : (
        items.map((item) => (
          <DesignCard
            key={item.id}
            item={item}
            canEdit={b.canEditSettings}
            draftHasLogos={draftHasLogos}
            flash={actions.flashId === item.id}
            onPreview={() =>
              b.setOverride({
                source: { source: "design", designId: item.id },
                label: `Предпросмотр дизайна «${item.name}»`,
                action: { label: "Применить ко всему сайту", run: () => void actions.apply(item) },
              })
            }
            onApply={() => void actions.apply(item)}
            onUpdate={() => setUpdating(item)}
            onRename={() => setRenaming(item)}
            onDelete={() => void actions.remove(item)}
          />
        ))
      )}
      <CreateDesignDialog
        open={creating}
        list={list}
        themeChanges={actions.themeChanges}
        onClose={() => setCreating(false)}
        onSubmit={actions.create}
        onReloadAll={() => void Promise.all([view.reload(), designs.reload()])}
      />
      <UpdateDesignDialog item={updating} list={list} onClose={() => setUpdating(null)} onSubmit={actions.update} />
      <SaveDesignDialog
        open={renaming !== null}
        renameFrom={renaming?.name ?? ""}
        draftDiffers={false}
        onClose={() => setRenaming(null)}
        onSubmit={async ({ name }) => {
          if (renaming) await designs.rename(renaming.id, name);
        }}
      />
    </div>
  );
}
